import {
  KEEPER_DRAFT_FIT_STRATEGY,
  type KeeperDraftFitRanking,
  type KeeperDraftTeamRoster,
} from "./keeper-draft-fit-canonical";

const DEFAULT_TIMEOUT_MS = 30_000;
let requestSequence = 0;

interface FitWorkerResponse extends Partial<KeeperDraftFitRanking> {
  requestId?: unknown;
  ok?: unknown;
  error?: unknown;
}

export interface KeeperDraftFitWorker {
  postMessage(message: unknown): void;
  terminate(): void;
  addEventListener(
    type: "message" | "error" | "messageerror",
    listener: EventListener,
  ): void;
  removeEventListener(
    type: "message" | "error" | "messageerror",
    listener: EventListener,
  ): void;
}

export type KeeperDraftFitWorkerFactory = () => KeeperDraftFitWorker;

function defaultWorkerFactory(): KeeperDraftFitWorker {
  return new Worker(
    new URL("./keeper-draft-fit.worker.ts", import.meta.url),
    { type: "module", name: "team-beer-keeper-draft-fit" },
  ) as unknown as KeeperDraftFitWorker;
}

function validatedUniqueIds(value: unknown): string[] | null {
  if (!Array.isArray(value) || value.some((id) => typeof id !== "string" || !id)) return null;
  const unique = [...new Set(value)];
  return unique.length === value.length ? unique : null;
}

function validatedRequestRosters(
  teamRosters: readonly KeeperDraftTeamRoster[],
): Array<{ teamName: string; keeperPlayerIds: string[] }> | null {
  if (!Array.isArray(teamRosters) || teamRosters.length === 0) return null;
  const teamNames = new Set<string>();
  const rosteredIds = new Set<string>();
  const validated = [];
  for (const roster of teamRosters) {
    if (!roster || typeof roster.teamName !== "string" || !roster.teamName || teamNames.has(roster.teamName)) {
      return null;
    }
    const keeperPlayerIds = validatedUniqueIds(roster.keeperPlayerIds);
    if (!keeperPlayerIds || keeperPlayerIds.some((playerId) => rosteredIds.has(playerId))) return null;
    teamNames.add(roster.teamName);
    keeperPlayerIds.forEach((playerId) => rosteredIds.add(playerId));
    validated.push({ teamName: roster.teamName, keeperPlayerIds });
  }
  return validated;
}

export function draftLabKeeperDraftFitsWithWorker(
  teamName: string,
  availablePlayerIds: readonly string[],
  teamRosters: readonly KeeperDraftTeamRoster[],
  workerFactory: KeeperDraftFitWorkerFactory = defaultWorkerFactory,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<KeeperDraftFitRanking> {
  return new Promise((resolve, reject) => {
    const requestedAvailableIds = validatedUniqueIds(availablePlayerIds);
    const requestedTeamRosters = validatedRequestRosters(teamRosters);
    if (!teamName || !requestedAvailableIds || !requestedTeamRosters) {
      reject(new Error("The authoritative Keeper Draft Fit input is invalid."));
      return;
    }
    const requestedTeam = requestedTeamRosters.find((roster) => roster.teamName === teamName);
    if (!requestedTeam) {
      reject(new Error("The selected team is missing from the authoritative keeper ledger."));
      return;
    }
    const rosteredIds = new Set(
      requestedTeamRosters.flatMap((roster) => roster.keeperPlayerIds),
    );
    if (requestedAvailableIds.some((playerId) => rosteredIds.has(playerId))) {
      reject(new Error("A player cannot be both rostered and available."));
      return;
    }

    let worker: KeeperDraftFitWorker;
    try {
      worker = workerFactory();
    } catch (caught) {
      reject(caught instanceof Error ? caught : new Error("The Draft Lab Fit worker could not start."));
      return;
    }
    const requestId = `keeper-draft-fit-${Date.now()}-${requestSequence += 1}`;
    let settled = false;
    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      worker.removeEventListener("message", onMessage);
      worker.removeEventListener("error", onError);
      worker.removeEventListener("messageerror", onMessageError);
      worker.terminate();
      callback();
    };
    const onMessage = ((event: MessageEvent<FitWorkerResponse>) => {
      const response = event.data;
      if (!response || response.requestId !== requestId) return;
      const rankedPlayerIds = validatedUniqueIds(response.rankedPlayerIds);
      const rows = Array.isArray(response.rows) ? response.rows : null;
      const availableSet = new Set(requestedAvailableIds);
      const validRows = rows?.every((row, index) => (
        row
        && typeof row.playerId === "string"
        && row.playerId === rankedPlayerIds?.[index]
        && Number.isFinite(row.score)
        && Number.isInteger(row.roundedFit)
        && row.roundedFit === Math.round(row.score)
        && Array.isArray(row.reasons)
        && row.reasons.every((reason) => typeof reason === "string")
      ));
      const valid = Boolean(
        response.ok === true
        && response.strategy === KEEPER_DRAFT_FIT_STRATEGY
        && response.teamName === teamName
        && response.canonicalTeamCount === requestedTeamRosters.length
        && response.currentRosterCount === requestedTeam.keeperPlayerIds.length
        && rankedPlayerIds
        && rankedPlayerIds.length === requestedAvailableIds.length
        && rankedPlayerIds.every((playerId) => availableSet.has(playerId))
        && rows
        && rows.length === rankedPlayerIds.length
        && validRows
      );
      if (valid) {
        finish(() => resolve({
          strategy: KEEPER_DRAFT_FIT_STRATEGY,
          teamName,
          canonicalTeamCount: response.canonicalTeamCount as number,
          currentRosterCount: response.currentRosterCount as number,
          rankedPlayerIds: rankedPlayerIds as string[],
          rows: rows as KeeperDraftFitRanking["rows"],
        }));
        return;
      }
      finish(() => reject(new Error(
        response.ok === false && typeof response.error === "string" && response.error.trim()
          ? response.error
          : "The Draft Lab Fit worker returned an invalid response.",
      )));
    }) as EventListener;
    const onError = (() => finish(() => reject(new Error(
      "The Draft Lab Fit worker stopped unexpectedly.",
    )))) as EventListener;
    const onMessageError = (() => finish(() => reject(new Error(
      "The Draft Lab Fit worker returned an unreadable response.",
    )))) as EventListener;
    const timeoutId = setTimeout(() => finish(() => reject(new Error(
      "Draft Lab Fit timed out.",
    ))), Math.max(1, timeoutMs));

    worker.addEventListener("message", onMessage);
    worker.addEventListener("error", onError);
    worker.addEventListener("messageerror", onMessageError);
    try {
      worker.postMessage({
        requestId,
        teamName,
        availablePlayerIds: requestedAvailableIds,
        teamRosters: requestedTeamRosters,
      });
    } catch (caught) {
      finish(() => reject(caught instanceof Error
        ? caught
        : new Error("The Draft Lab Fit request could not be sent.")));
    }
  });
}

export function draftLabKeeperDraftFits(
  teamName: string,
  availablePlayerIds: readonly string[],
  teamRosters: readonly KeeperDraftTeamRoster[],
): Promise<KeeperDraftFitRanking> {
  return draftLabKeeperDraftFitsWithWorker(teamName, availablePlayerIds, teamRosters);
}
